//@flow
import React, {useEffect, useRef, useMemo} from 'react';
import {
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
  Tooltip,
  IconButton,
  FormHelperText,
} from '@material-ui/core';

import {useFormContext, useWatch} from 'react-hook-form';

import CustomTextField from '../../../elements/CustomTextField/CustomTextField';
import {useAdminAddress} from '../../address/utils/useAdminAddress';
import CustomSelect from '../../../elements/CustomSelect/CustomSelect';
import {useStoreCountry} from '../../country/utils';
import {getConfigurationAdmin} from '../../../utils/location';
import {useStoreTaxInvoiceType} from '../../tax-invoice/utils';

import type {ManualOrderShippingData} from './ManualOrderCheckout';
import {RefreshRounded} from '@material-ui/icons';
import {DELIVERY_FORM_FIELDS} from './delivery-form/constant';
import CustomButton from '../../../elements/CustomButton/CustomButton';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: '1rem',
    marginTop: '1rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '0.875rem',
    textAlign: 'center',
  },
}));

export const ORDER_TAX_INVOICE_FORM_FIELDS = {
  taxInvoiceType: 'TAX_INVOICE_TYPE',
  companyName: 'COMPANY_NAME',
  ruc: 'RUC',
  address: 'TAX_ADDRESS',
  admin1: 'TAX_ADMIN1',
  admin2: 'TAX_ADMIN2',
  admin3: 'TAX_ADMIN3',
};

export type TaxInvoiceInformationFormDefaultValues = {
  taxInvoiceType?: string,
  companyName?: string,
  ruc?: string,
  address?: string,
  admin1Id?: string,
  admin2Id?: string,
  admin3Id?: string,
};

type Props = {
  defaultValues?: TaxInvoiceInformationFormDefaultValues,
  manualOrderShippingData?: ManualOrderShippingData,
};

export default function TaxInvoiceInformationForm(props: Props) {
  const {defaultValues} = props;

  const classes = useStyles();

  const {storeTaxInvoiceType} = useStoreTaxInvoiceType();
  const noTaxInvoice = storeTaxInvoiceType.length === 0;

  const {
    register,
    setValue,
    watch,
    errors,
    unregister,
    clearErrors,
    setError,
  } = useFormContext();

  const alreadySetDefaultValues = useRef(false);
  const alreadySetDefaultAdmin1 = useRef(false);
  const alreadySetDefaultAdmin2 = useRef(false);
  const alreadySetDefaultAdmin3 = useRef(false);

  const {
    admin1List,
    admin2List,
    admin3List,
    selectedAdmin1,
    selectedAdmin2,
    selectedAdmin3,
    selectAdmin2,
    selectAdmin3,
    selectAdmin1,
    loading,
    setAdmins,
  } = useAdminAddress();

  const deliveryMethodSelected = useWatch({
    name: DELIVERY_FORM_FIELDS.DELIVERY_METHOD,
  });

  const shippingAddress = useWatch({
    name: DELIVERY_FORM_FIELDS.ADDRESS_1,
  });

  const shippingAdmin1 = useWatch({
    name: DELIVERY_FORM_FIELDS.ADMIN1,
  });

  const shippingAdmin2 = useWatch({
    name: DELIVERY_FORM_FIELDS.ADMIN2,
  });

  const shippingAdmin3 = useWatch({
    name: DELIVERY_FORM_FIELDS.ADMIN3,
  });

  const country = useStoreCountry();
  const configAdmin = getConfigurationAdmin(country?.code);

  useEffect(() => {
    if (noTaxInvoice) return;
    register(ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType, {
      required: 'Campo requerido',
    });
    register(ORDER_TAX_INVOICE_FORM_FIELDS.companyName, {
      required: 'Campo requerido',
    });
    register(ORDER_TAX_INVOICE_FORM_FIELDS.ruc, {
      required: 'Campo requerido',
      minLength: {
        value: 8,
        message: 'Documento inválido',
      },
      maxLength: {
        value: 11,
        message: 'Documento inválido',
      },
    });
    register(ORDER_TAX_INVOICE_FORM_FIELDS.address, {
      required: 'Campo requerido',
    });
    register(ORDER_TAX_INVOICE_FORM_FIELDS.admin1, {
      required: 'Campo requerido',
    });
    register(ORDER_TAX_INVOICE_FORM_FIELDS.admin2, {
      required: 'Campo requerido',
    });
    register(ORDER_TAX_INVOICE_FORM_FIELDS.admin3);
  }, [noTaxInvoice, register, unregister]);

  useEffect(() => {
    if (storeTaxInvoiceType.length === 1) {
      setValue(
        ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType,
        storeTaxInvoiceType[0].name
      );
    }
  }, [setValue, storeTaxInvoiceType]);

  useEffect(() => {
    if (!defaultValues || noTaxInvoice) return;
    if (alreadySetDefaultValues.current) return;

    const {taxInvoiceType, companyName, ruc, address} = defaultValues;

    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType, taxInvoiceType);

    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.companyName, companyName);
    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.ruc, ruc);

    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.address, address);

    alreadySetDefaultValues.current = true;
  }, [defaultValues, noTaxInvoice, setValue]);

  useEffect(() => {
    if (alreadySetDefaultAdmin1.current) return;
    if (!defaultValues || !admin1List.length) return;
    const {admin1Id} = defaultValues;
    if (admin1Id || admin1Id?.toString()) {
      const admin1 = admin1List.find(admin1 => admin1.adminId === admin1Id);
      if (!admin1) return;
      setValue(ORDER_TAX_INVOICE_FORM_FIELDS.admin1, admin1Id);
      selectAdmin1(admin1);
      alreadySetDefaultAdmin1.current = true;
    }
  }, [admin1List, defaultValues, selectAdmin1, setValue]);

  useEffect(() => {
    if (alreadySetDefaultAdmin2.current) return;
    if (!defaultValues || !admin2List.length) return;
    const {admin2Id} = defaultValues;
    if (admin2Id || admin2Id?.toString()) {
      const admin2 = admin2List.find(admin2 => admin2.adminId === admin2Id);
      if (!admin2) return;
      setValue(ORDER_TAX_INVOICE_FORM_FIELDS.admin2, admin2Id);
      selectAdmin2(admin2);
      alreadySetDefaultAdmin2.current = true;
    }
  }, [admin2List, defaultValues, selectAdmin2, setValue]);

  useEffect(() => {
    if (alreadySetDefaultAdmin3.current) return;
    if (!defaultValues || !admin3List.length) return;
    const {admin3Id} = defaultValues;
    if (admin3Id || admin3Id?.toString()) {
      const admin3 = admin3List.find(admin3 => admin3.adminId === admin3Id);
      if (!admin3) return;
      setValue(ORDER_TAX_INVOICE_FORM_FIELDS.admin3, admin3Id);
      selectAdmin3(admin3);
      alreadySetDefaultAdmin3.current = true;
    }
  }, [admin3List, defaultValues, selectAdmin3, setValue]);

  useEffect(() => {
    if (!selectedAdmin1) return;
    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.admin1, selectedAdmin1.adminId);
    clearErrors(ORDER_TAX_INVOICE_FORM_FIELDS.admin1);
  }, [clearErrors, selectedAdmin1, setValue]);

  useEffect(() => {
    if (!selectedAdmin2) return;
    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.admin2, selectedAdmin2.adminId);
    clearErrors(ORDER_TAX_INVOICE_FORM_FIELDS.admin2);
  }, [clearErrors, selectedAdmin2, setValue]);

  useEffect(() => {
    if (!selectedAdmin3) return;
    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.admin3, selectedAdmin3.adminId);
    clearErrors(ORDER_TAX_INVOICE_FORM_FIELDS.admin3);
  }, [clearErrors, selectedAdmin3, setValue]);

  const watchTaxInvoiceType = watch(
    ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType
  );

  const taxInvoiceSelected = useMemo(() => {
    return storeTaxInvoiceType.find(item => item.name === watchTaxInvoiceType);
  }, [storeTaxInvoiceType, watchTaxInvoiceType]);

  const canAutoCompleteAddress = useMemo(() => {
    if (deliveryMethodSelected !== 'SHIPPING') return false;
    return Boolean(
      shippingAddress || shippingAdmin1 || shippingAdmin2 || shippingAdmin3
    );
  }, [
    deliveryMethodSelected,
    shippingAddress,
    shippingAdmin1,
    shippingAdmin2,
    shippingAdmin3,
  ]);

  console.log({canAutoCompleteAddress});

  if (noTaxInvoice) {
    return (
      <div className={classes.root}>
        <Typography className={classes.title}>
          No hay tipos de comprobantes disponibles
        </Typography>
        <aside className={classes.content}>
          <Typography className={classes.subtitle}>
            <span>
              Ingreas a tu configuración de ventas y verifica que esten activos
            </span>
            <CustomButton
              target="_blank"
              rel="noopener noreferrer"
              to="/configure/sales-process"
              variant="text"
            >
              Ir a configuración
            </CustomButton>
          </Typography>
        </aside>
      </div>
    );
  }

  const watchAddress = watch(ORDER_TAX_INVOICE_FORM_FIELDS.address, '');
  const watchRuc = watch(ORDER_TAX_INVOICE_FORM_FIELDS.ruc, '');
  const watchCompanyName = watch(ORDER_TAX_INVOICE_FORM_FIELDS.companyName, '');

  const handleChangeField = (name: string, value: any) => {
    setValue(name, value);
    clearErrors(name);
  };

  const documentLength = taxInvoiceSelected?.name === 'FACTURA' ? 11 : 8;

  const handleChangeRuc = (ruc: string) => {
    const rucValue = ruc.replace(/\D/g, '');
    if (rucValue.length > documentLength) return;

    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.ruc, rucValue);

    if (rucValue.length === documentLength) {
      clearErrors(ORDER_TAX_INVOICE_FORM_FIELDS.ruc);
    } else {
      setError(ORDER_TAX_INVOICE_FORM_FIELDS.ruc, {
        type: 'manual',
        message: `Documento inválido`,
      });
    }
  };

  const handleAutoCompleteAddress = () => {
    if (!canAutoCompleteAddress) return;
    setValue(ORDER_TAX_INVOICE_FORM_FIELDS.address, shippingAddress);
    if (shippingAddress?.trim().length > 0) {
      clearErrors(ORDER_TAX_INVOICE_FORM_FIELDS.address);
    }

    if (shippingAdmin1)
      setAdmins(
        shippingAdmin1.adminId,
        shippingAdmin2?.adminId,
        shippingAdmin3?.adminId
      );
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>Tipo de comprobante</Typography>
      <aside className={classes.content}>
        <RadioGroup row name="taxInvoiceType" className={classes.radioGroup}>
          {storeTaxInvoiceType.map(item => {
            return (
              <div className={classes.radioWrap} key={item.name}>
                <FormControlLabel
                  classes={{
                    root: classes.deliveryRadio,
                  }}
                  control={
                    <Radio
                      color="primary"
                      value={item.name}
                      checked={watchTaxInvoiceType === item.name}
                      onChange={e => {
                        setValue(
                          ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType,
                          e.target.value
                        );
                        clearErrors(
                          ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType
                        );
                        setValue(ORDER_TAX_INVOICE_FORM_FIELDS.ruc, '');
                      }}
                      className={classes.deliveryRadioIcon}
                    />
                  }
                  label={item.label}
                />
              </div>
            );
          })}
        </RadioGroup>

        {errors[ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType] && (
          <FormHelperText error>
            {errors[ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType]?.message}
          </FormHelperText>
        )}

        {!!taxInvoiceSelected && (
          <>
            <CustomTextField
              label={
                taxInvoiceSelected?.name === 'FACTURA'
                  ? 'Razón Social'
                  : 'Nombres'
              }
              name={ORDER_TAX_INVOICE_FORM_FIELDS.companyName}
              onInput={e => {
                handleChangeField(
                  ORDER_TAX_INVOICE_FORM_FIELDS.companyName,
                  e.target.value
                );
              }}
              helperText={
                errors[ORDER_TAX_INVOICE_FORM_FIELDS.companyName]?.message
              }
              value={watchCompanyName}
              error={!!errors[ORDER_TAX_INVOICE_FORM_FIELDS.companyName]}
            />
            <CustomTextField
              label={taxInvoiceSelected?.name === 'FACTURA' ? 'RUC' : 'DNI'}
              name={ORDER_TAX_INVOICE_FORM_FIELDS.ruc}
              onInput={e => {
                handleChangeRuc(e.target.value);
              }}
              value={watchRuc}
              helperText={
                errors[ORDER_TAX_INVOICE_FORM_FIELDS.ruc] &&
                `${errors[ORDER_TAX_INVOICE_FORM_FIELDS.ruc].message}, 
                ${documentLength} dígitos requeridos`
              }
              error={!!errors[ORDER_TAX_INVOICE_FORM_FIELDS.ruc]}
            />

            <Typography className={classes.title}>
              <span>Información de Dirección</span>

              {canAutoCompleteAddress && (
                <Tooltip placement="top" title="Usar los datos de entrega">
                  <IconButton size="small" onClick={handleAutoCompleteAddress}>
                    <RefreshRounded />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>

            <CustomTextField
              label="Dirección"
              name={ORDER_TAX_INVOICE_FORM_FIELDS.address}
              onInput={e => {
                handleChangeField(
                  ORDER_TAX_INVOICE_FORM_FIELDS.address,
                  e.target.value
                );
              }}
              value={watchAddress}
              helperText={
                errors[ORDER_TAX_INVOICE_FORM_FIELDS.address]?.message
              }
              error={!!errors[ORDER_TAX_INVOICE_FORM_FIELDS.address]}
            />

            <CustomSelect
              value={selectedAdmin1?.adminName || ''}
              label={configAdmin.admin1.label}
              onChange={e => {
                const adminOption = admin1List.find(
                  admin1 => admin1.adminName === e.target.value
                );
                if (!adminOption) return;

                handleChangeField(
                  ORDER_TAX_INVOICE_FORM_FIELDS.admin1,
                  adminOption.adminId
                );
                selectAdmin1(adminOption);
              }}
              helperText={errors[ORDER_TAX_INVOICE_FORM_FIELDS.admin1]?.message}
              error={!!errors[ORDER_TAX_INVOICE_FORM_FIELDS.admin1]}
              disabled={loading || admin1List.length === 0}
            >
              {admin1List.map((adminAddress, index: number) => (
                <MenuItem
                  key={adminAddress.adminId}
                  value={adminAddress.adminName}
                >
                  {adminAddress.adminName}
                </MenuItem>
              ))}
            </CustomSelect>

            <CustomSelect
              value={selectedAdmin2?.adminName || ''}
              label={configAdmin.admin2.label}
              onChange={e => {
                const adminOption = admin2List.find(
                  adminAddress => adminAddress.adminName === e.target.value
                );
                if (!adminOption) return;
                handleChangeField(
                  ORDER_TAX_INVOICE_FORM_FIELDS.admin2,
                  adminOption.adminId
                );
                selectAdmin2(adminOption);
              }}
              helperText={errors[ORDER_TAX_INVOICE_FORM_FIELDS.admin2]?.message}
              error={!!errors[ORDER_TAX_INVOICE_FORM_FIELDS.admin2]}
              disabled={loading || admin2List.length === 0}
            >
              {admin2List.map((adminAddress, index: number) => (
                <MenuItem
                  key={adminAddress.adminId}
                  value={adminAddress.adminName}
                >
                  {adminAddress.adminName}
                </MenuItem>
              ))}
            </CustomSelect>

            <CustomSelect
              value={selectedAdmin3?.adminName || ''}
              label={configAdmin.admin3.label}
              onChange={e => {
                const adminOption = admin3List.find(
                  adminAddress => adminAddress.adminName === e.target.value
                );
                if (!adminOption) return;
                handleChangeField(
                  ORDER_TAX_INVOICE_FORM_FIELDS.admin3,
                  adminOption.adminId
                );
                selectAdmin3(adminOption);
              }}
              helperText={errors[ORDER_TAX_INVOICE_FORM_FIELDS.admin3]?.message}
              error={!!errors[ORDER_TAX_INVOICE_FORM_FIELDS.admin3]}
              disabled={loading || admin3List.length === 0}
            >
              {admin3List.map((adminAddress, index: number) => (
                <MenuItem
                  key={adminAddress.adminId}
                  value={adminAddress.adminName}
                >
                  {adminAddress.adminName}
                </MenuItem>
              ))}
            </CustomSelect>
          </>
        )}
      </aside>
    </div>
  );
}
